// src/app/tools/random-ip/page.tsx
'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Copy, RefreshCw, Check, Network } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomIpGeneratorContent } from '../../data/random-ip-generator-content';
import Link from 'next/link';

type IPVersion = 'ipv4' | 'ipv6';
type IPFormat = 'decimal' | 'hex' | 'binary';

export default function RandomIPGenerator() {
    const [ipVersion, setIpVersion] = useState<IPVersion>('ipv4');
    const [ipFormat, setIpFormat] = useState<IPFormat>('decimal');
    const [customRange, setCustomRange] = useState<boolean>(false);
    const [ipRanges, setIpRanges] = useState({
        ipv4: { min: '0.0.0.0', max: '255.255.255.255' },
        ipv6: { min: '2000::', max: '3fff:ffff:ffff:ffff:ffff:ffff:ffff:ffff' }
    });
    const [result, setResult] = useState<string>('');
    const [copied, setCopied] = useState<boolean>(false);
    const [history, setHistory] = useState<string[]>([]);
    const { toast } = useToast();

    // Generate a random number within a range
    const getRandomInRange = (min: number, max: number): number => {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    // Convert a number to binary string with leading zeros
    const toBinary = (num: number, bits: number): string => {
        return num.toString(2).padStart(bits, '0');
    };

    // Generate a random IPv4 address
    const generateIPv4 = useCallback(() => {
        let minOctets = [0, 0, 0, 0];
        let maxOctets = [255, 255, 255, 255];

        if (customRange) {
            minOctets = ipRanges.ipv4.min.split('.').map(Number);
            maxOctets = ipRanges.ipv4.max.split('.').map(Number);
        }

        const octets = minOctets.map((min, i) =>
            getRandomInRange(min, maxOctets[i])
        );

        if (ipFormat === 'hex') {
            return octets.map(octet =>
                '0x' + octet.toString(16).padStart(2, '0')
            ).join('.');
        } else if (ipFormat === 'binary') {
            return octets.map(octet =>
                toBinary(octet, 8)
            ).join('.');
        }
        return octets.join('.');
    }, [customRange, ipFormat, ipRanges.ipv4]);

    // Generate a random IPv6 address
    const generateIPv6 = useCallback(() => {
        const segments = 8;
        let minSegments = Array(segments).fill('0');
        let maxSegments = Array(segments).fill('ffff');

        if (customRange) {
            minSegments = ipRanges.ipv6.min.split(':').filter(Boolean);
            maxSegments = ipRanges.ipv6.max.split(':').filter(Boolean);
        }

        const randomSegments = minSegments.map((min, i) => {
            const minVal = parseInt(min || '0', 16);
            const maxVal = parseInt(maxSegments[i] || 'ffff', 16);
            return getRandomInRange(minVal, maxVal).toString(16).padStart(4, '0');
        });

        if (ipFormat === 'decimal') {
            return randomSegments.join(':');
        } else if (ipFormat === 'binary') {
            return randomSegments.map(seg =>
                toBinary(parseInt(seg, 16), 16)
            ).join(':');
        }
        return randomSegments.join(':');
    }, [customRange, ipFormat, ipRanges.ipv6]);

    // Generate a random IP address
    const generateRandomIP = useCallback(() => {
        const ip = ipVersion === 'ipv4' ? generateIPv4() : generateIPv6();
        setResult(ip);
        setHistory(prev => [ip, ...prev].slice(0, 5)); // Keep last 5 results
        return ip;
    }, [ipVersion, generateIPv4, generateIPv6]);

    // Copy IP to clipboard
    const handleCopy = useCallback(() => {
        if (!result) return;

        navigator.clipboard.writeText(result);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'IP address has been copied to clipboard.',
        });

        setTimeout(() => setCopied(false), 2000);
    }, [result, toast]);

    // Handle range input change
    const handleRangeChange = (version: IPVersion, type: 'min' | 'max', value: string) => {
        setIpRanges(prev => ({
            ...prev,
            [version]: {
                ...prev[version],
                [type]: value
            }
        }));
    };

    // Generate initial IP on mount and when dependencies change
    useEffect(() => {
        generateRandomIP();
    }, [generateRandomIP]);

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8 text-center">
                    <ToolPageIntro title="Random IP Generator">
                Generate random IP addresses with our free online tool. Create IPv4, IPv6, and custom IP ranges for testing, network simulation, and development.
            </ToolPageIntro>
                </div>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Configuration</CardTitle>
                            <CardDescription>Customize your random IP generation</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <Label className="block mb-2">IP Version</Label>
                                <Tabs
                                    value={ipVersion}
                                    onValueChange={(value) => setIpVersion(value as IPVersion)}
                                    className="w-full"
                                >
                                    <TabsList className="grid w-full grid-cols-2">
                                        <TabsTrigger value="ipv4">IPv4</TabsTrigger>
                                        <TabsTrigger value="ipv6">IPv6</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>

                            <div>
                                <Label className="block mb-2">Display Format</Label>
                                <Tabs
                                    value={ipFormat}
                                    onValueChange={(value) => setIpFormat(value as IPFormat)}
                                    className="w-full"
                                >
                                    <TabsList className="grid w-full grid-cols-3">
                                        <TabsTrigger value="decimal">Decimal</TabsTrigger>
                                        <TabsTrigger value="hex">Hexadecimal</TabsTrigger>
                                        <TabsTrigger value="binary">Binary</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                <div className="space-y-1">
                                    <Label>Custom Range</Label>
                                    <p className="text-sm text-muted-foreground">
                                        {customRange ? 'Using custom IP range' : 'Using full IP range'}
                                    </p>
                                </div>
                                <Switch
                                    checked={customRange}
                                    onCheckedChange={setCustomRange}
                                />
                            </div>

                            {customRange && (
                                <div className="space-y-4 pt-2">
                                    <div>
                                        <Label className="block mb-2">
                                            {ipVersion === 'ipv4' ? 'IPv4 Range' : 'IPv6 Range'}
                                        </Label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Start {ipVersion === 'ipv4' ? 'IP' : 'Address'}</Label>
                                                <Input
                                                    value={ipRanges[ipVersion].min}
                                                    onChange={(e) => handleRangeChange(ipVersion, 'min', e.target.value)}
                                                    placeholder={ipVersion === 'ipv4' ? '0.0.0.0' : '2000::'}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>End {ipVersion === 'ipv4' ? 'IP' : 'Address'}</Label>
                                                <Input
                                                    value={ipRanges[ipVersion].max}
                                                    onChange={(e) => handleRangeChange(ipVersion, 'max', e.target.value)}
                                                    placeholder={ipVersion === 'ipv4' ? '255.255.255.255' : '3fff:ffff:ffff:ffff:ffff:ffff:ffff:ffff'}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="pt-2">
                                <Button
                                    onClick={generateRandomIP}
                                    className="w-full"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate New IP
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-center">
                                <CardTitle>Generated IP</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!result}
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div
                                className="bg-muted/50 p-6 rounded-md text-center cursor-pointer hover:bg-muted/70 transition-colors"
                                onClick={handleCopy}
                            >
                                {result ? (
                                    <div className="text-2xl font-mono font-bold break-all">
                                        {result}
                                    </div>
                                ) : (
                                    <div className="text-muted-foreground">Click generate to get started</div>
                                )}
                            </div>

                            {history.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-sm font-medium mb-2">Recent IPs</h3>
                                    <div className="flex flex-col gap-1">
                                        {history.map((ip, index) => (
                                            <div
                                                key={index}
                                                className="px-3 py-2 rounded-md bg-muted/50 hover:bg-muted/70 transition-colors cursor-pointer font-mono text-sm"
                                                onClick={() => {
                                                    setResult(ip);
                                                    navigator.clipboard.writeText(ip);
                                                    toast({
                                                        title: 'Copied!',
                                                        description: 'IP address has been copied to clipboard.',
                                                    });
                                                }}
                                            >
                                                {ip}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Quick Presets</CardTitle>
                            <CardDescription>Common IP ranges</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setIpVersion('ipv4');
                                        setIpRanges(prev => ({
                                            ...prev,
                                            ipv4: { min: '192.168.0.0', max: '192.168.255.255' }
                                        }));
                                        setCustomRange(true);
                                    }}
                                >
                                    Private IPv4 (192.168.x.x)
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setIpVersion('ipv4');
                                        setIpRanges(prev => ({
                                            ...prev,
                                            ipv4: { min: '10.0.0.0', max: '10.255.255.255' }
                                        }));
                                        setCustomRange(true);
                                    }}
                                >
                                    Private IPv4 (10.x.x.x)
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setIpVersion('ipv6');
                                        setIpRanges(prev => ({
                                            ...prev,
                                            ipv6: { min: 'fc00::', max: 'fdff:ffff:ffff:ffff:ffff:ffff:ffff:ffff' }
                                        }));
                                        setCustomRange(true);
                                    }}
                                >
                                    Private IPv6 (fc00::/7)
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setIpVersion('ipv6');
                                        setIpRanges(prev => ({
                                            ...prev,
                                            ipv6: { min: '2000::', max: '3fff:ffff:ffff:ffff:ffff:ffff:ffff:ffff' }
                                        }));
                                        setCustomRange(true);
                                    }}
                                >
                                    Global Unicast IPv6 (2000::/3)
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        This tool generates random IP addresses based on your specified parameters.
                        All processing happens in your browser - no data is sent to any server.
                    </p>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomIpGeneratorContent.title}
                description={randomIpGeneratorContent.description}
                content={randomIpGeneratorContent.content}
                faqs={randomIpGeneratorContent.faqs}
                relatedTools={randomIpGeneratorContent.relatedTools}
            />
        </div>
    );
}